import { axiosPut, del, get, post } from "helpers/api_helper"
import { call, put, takeEvery } from "redux-saga/effects"
import { getApiErrorMessage, getFormDataFromJSON, showToast } from "utils"
import {
  cloneProductFailed,
  cloneProductSuccess,
  createProductCategoryFailed,
  createProductCategorySuccess,
  createProductFailed,
  createProductSuccess,
  deleteProductFailed,
  deleteProductSuccess,
  getCategoryForFormFailed,
  getCategoryForFormSuccess,
  getFeatureRecipientsFailed,
  getFeatureRecipientsSuccess,
  getOrderDetailsFailed,
  getOrderDetailsSuccess,
  getOrderFailed,
  getOrderSuccess,
  getProductCategoryFailed,
  getProductCategorySuccess,
  getProductFailed,
  getProductOrdersFailed,
  getProductOrdersSuccess,
  getProductsFailed,
  getProductsSuccess,
  getProductSuccess,
  getProductUserTagsFailed,
  getProductUserTagsSuccess,
  getStripePricesFailed,
  getStripePricesSuccess,
  getStripeProductsFailed,
  getStripeProductsSuccess,
  getTeaserProductsFailed,
  getTeaserProductsSuccess,
  hideCloneProductModal,
  hideDeleteProductModal,
  setOrderCheckoutSession,
  submitOrderFormFailed,
  submitOrderFormSuccess,
  updateProductCategoryFailed,
  updateProductCategorySuccess,
  updateProductFailed,
  updateProductSuccess,
} from "./actions"
import * as url from "helpers/url_helper"
import {
  CLONE_PRODUCT,
  CREATE_PRODUCT,
  CREATE_PRODUCT_CATEGORY,
  DELETE_PRODUCT,
  GET_CATEGORY_FOR_FORM,
  GET_FEATURE_RECIPIENTS,
  GET_ORDER,
  GET_ORDER_DETAILS,
  GET_PRODUCT,
  GET_PRODUCTS,
  GET_PRODUCT_CATEGORY,
  GET_PRODUCT_ORDERS,
  GET_PRODUCT_USER_TAGS,
  GET_STRIPE_PRICES,
  GET_STRIPE_PRODUCTS,
  GET_TEASER_PRODUCTS,
  SUBMIT_ORDER_FORM,
  UPDATE_PRODUCT,
  UPDATE_PRODUCT_CATEGORY,
} from "./actionTypes"
import axios from "axios"
import { app } from "config"
import { URL_PATHS } from "constants/index"

function* getProductOrders({ payload: params }) {
  try {
    const response = yield get(url.ADMIN_ORDER_URL, { params })
    yield put(getProductOrdersSuccess(response))
  } catch (error) {
    yield put(getProductOrdersFailed(error))
    showToast("error", error, "Error")
  }
}

function* getProducts({ payload: params }) {
  try {
    const response = yield get(url.PRODUCT_URL, { params })
    yield put(getProductsSuccess(response))
  } catch (error) {
    yield put(getProductsFailed(error))
    showToast("error", error, "Error")
  }
}

function* getStripeProducts({ payload: params }) {
  try {
    const response = yield get(url.STRIPE_PRODUCTS_URL, { params })
    yield put(getStripeProductsSuccess(response))
  } catch (error) {
    yield put(getStripeProductsFailed(error))
    showToast("error", error, "Error")
  }
}

function* getStripePrices({ payload: params }) {
  try {
    const response = yield get(url.STRIPE_PRICES_URL, { params })
    yield put(getStripePricesSuccess(response))
  } catch (error) {
    yield put(getStripePricesFailed(error))
    showToast("error", error, "Error")
  }
}

function* createProduct({ payload: params }) {
  try {
    const response = yield post(url.ADMIN_PRODUCT_URL, params?.values)
    yield post(url.FEATURE_RECIPIENTS_URL, {
      feature_id: response?.id,
      feature_type: "product",
      user_ids: params?.user_ids?.map(x => x?.value) || [],
      vendor_id: parseInt(response?.vendor_id),
    })
    if (params?.callback) {
      params?.callback()
    }
    yield put(
      createProductSuccess({
        values: { ...params?.values, id: response?.id },
        user_ids: params?.user_ids,
      })
    )
    showToast("success", "Product created!", "Success")
  } catch ({ response }) {
    const message = getApiErrorMessage(response)
    yield put(createProductFailed(message))
    showToast("error", message, "Error")
  }
}

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

function* deleteProduct({ payload: params }) {
  try {
    const response = yield del(`${url.ADMIN_PRODUCT_URL}/${params?.id}`)
    if (params?.callback) {
      yield call(wait, 1000)
      params?.callback()
    }
    yield put(hideDeleteProductModal())
    yield put(deleteProductSuccess(response))
    showToast("success", "Product deleted!", "Success")
  } catch (error) {
    yield put(deleteProductFailed(error))
    yield put(hideDeleteProductModal())
    showToast("error", error, "Error")
  }
}

function* getProduct({ payload: params }) {
  try {
    const response = yield get(`${url.ADMIN_PRODUCT_URL}/${params?.id}`)
    yield put(getProductSuccess(response))
  } catch (error) {
    yield put(getProductFailed(error))
    showToast("error", error, "Error")
  }
}

function* getFeatureRecipients({ payload: params }) {
  try {
    const response = yield get(url.FEATURE_RECIPIENTS_URL, { params })
    yield put(getFeatureRecipientsSuccess(response))
  } catch (error) {
    yield put(getFeatureRecipientsFailed(error))
    showToast("error", error, "Error")
  }
}

function* updateProduct({ payload: params }) {
  try {
    yield axiosPut(
      `${url.ADMIN_PRODUCT_URL}/${params?.values?.id}`,
      params?.values
    )
    yield post(url.FEATURE_RECIPIENTS_URL, {
      feature_id: params?.values?.id,
      feature_type: "product",
      user_ids: params?.user_ids?.map(x => x?.value) || [],
      vendor_id: parseInt(params?.values?.vendor_id),
    })
    yield put(updateProductSuccess(params))
    showToast("success", "Product Updated!", "Success")
  } catch ({ response }) {
    const message = getApiErrorMessage(response)
    yield put(updateProductFailed(message))
    showToast("error", message, "Error")
  }
}

function* createProductCategory({ payload: params }) {
  try {
    const response = yield post(
      url.GET_CATEGORIES_URL,
      getFormDataFromJSON(params)
    )
    const category = yield get(`${url.GET_CATEGORIES_URL}/${response?.id}`)
    yield put(createProductCategorySuccess(category))
    showToast("success", "Product Group Created!", "Success")
  } catch (error) {
    yield put(createProductCategoryFailed(error))
    showToast("error", error, "Error")
  }
}

function* updateProductCategory({ payload: params }) {
  try {
    yield post(
      `${url.GET_CATEGORIES_URL}/${params?.id}`,
      getFormDataFromJSON(params?.values)
    )
    yield call(wait, 1000)
    const response = yield get(`${url.GET_CATEGORIES_URL}/${params?.id}`)
    yield put(updateProductCategorySuccess(response))
    showToast("success", "Product Group Updated!", "Success")
  } catch (error) {
    yield put(updateProductCategoryFailed(error))
    showToast("error", error, "Error")
  }
}

function* getProductCategory({ payload: params }) {
  try {
    const response = yield get(`${url.GET_CATEGORIES_URL}/${params?.id}`)
    yield put(getProductCategorySuccess(response))
  } catch (error) {
    yield put(getProductCategoryFailed(error))
    showToast("error", error, "Error")
  }
}

function* getOrderDetails({ payload: params }) {
  try {
    const response = yield get(`/admin/order/${params?.id}`)
    yield put(getOrderDetailsSuccess(response))
  } catch (error) {
    yield put(getOrderDetailsFailed(error))
    showToast("error", error, "Error")
  }
}

const APP_API_URL = `${app.APP_LAYER}/v${app.APP_VERSION}`

async function fetchData({ url, params }) {
  try {
    const res = await axios.get(APP_API_URL + url, {
      params,
    })
    return res.data
  } catch (err) {
    throw err
  }
}

function* getCategoryForForm({ payload: params }) {
  try {
    // const response = yield call(fetchData, {
    //   url: url.CATEGORY_FOR_FORM_URL,
    //   params: params,
    // })
    const response = yield get(url.CATEGORY_FOR_FORM_URL, { params })
    yield put(getCategoryForFormSuccess(response))
  } catch (error) {
    yield put(getCategoryForFormFailed(error))
    showToast("error", error, "Error")
  }
}

function* getTeaserProducts({ payload: params }) {
  try {
    // const response = yield call(fetchData, {
    //   url: url.TEASER_PRODUCTS_URL,
    //   params: params,
    // })
    const response = yield get(url.TEASER_PRODUCTS_URL, { params })
    yield put(getTeaserProductsSuccess(response))
  } catch (error) {
    yield put(getTeaserProductsFailed(error))
    showToast("error", error, "Error")
  }
}

function* submitOrderForm({ payload: params }) {
  try {
    const response = yield post(url.ORDER_CHECKOUT_URL, params)
    yield put(submitOrderFormSuccess(response))
    if (response?.no_payment && response?.order_id) {
      window.location.href =
        URL_PATHS.CHECKOUT_SUCCESS +
        "?order_id=" +
        response?.order_id +
        "&vendor_id=" +
        params?.vendor_id
    }
    if (response?.url) {
      yield put(setOrderCheckoutSession(response))
      window.open(response?.url)
    }
  } catch (error) {
    yield put(submitOrderFormFailed(error))
    showToast("error", error, "Error")
  }
}

function* getOrder({ payload: params }) {
  try {
    const response = yield get(url.ORDER_URL, { params })
    yield put(getOrderSuccess(response))
  } catch (error) {
    yield put(getOrderFailed(error))
    showToast("error", error, "Error")
  }
}

function* getProductUserTags({ payload: params }) {
  try {
    const response = yield get(url.PRODUCT_USER_TAGS_URL, { params })
    yield put(getProductUserTagsSuccess(response))
  } catch ({ response }) {
    yield put(getProductUserTagsFailed(response))
    showToast("error", response, "Error")
  }
}

function* cloneProduct({ payload: params }) {
  try {
    const response = yield post(`${url.ADMIN_PRODUCT_URL}/${params?.id}/clone`)
    if (params?.callback) {
      yield call(wait, 1000)
      params?.callback()
    }
    yield put(cloneProductSuccess(response))
    yield put(hideCloneProductModal())
    showToast("success", "Product cloned!", "Success")
  } catch (error) {
    yield put(cloneProductFailed(error))
    yield put(hideCloneProductModal())
    showToast("error", error, "Error")
  }
}

function* productsAndSubscriptionsSaga() {
  yield takeEvery(GET_PRODUCT_ORDERS, getProductOrders)
  yield takeEvery(GET_PRODUCTS, getProducts)
  yield takeEvery(GET_STRIPE_PRODUCTS, getStripeProducts)
  yield takeEvery(GET_STRIPE_PRICES, getStripePrices)
  yield takeEvery(CREATE_PRODUCT, createProduct)
  yield takeEvery(DELETE_PRODUCT, deleteProduct)
  yield takeEvery(GET_PRODUCT, getProduct)
  yield takeEvery(GET_FEATURE_RECIPIENTS, getFeatureRecipients)
  yield takeEvery(UPDATE_PRODUCT, updateProduct)
  yield takeEvery(CREATE_PRODUCT_CATEGORY, createProductCategory)
  yield takeEvery(UPDATE_PRODUCT_CATEGORY, updateProductCategory)
  yield takeEvery(GET_PRODUCT_CATEGORY, getProductCategory)
  yield takeEvery(GET_ORDER_DETAILS, getOrderDetails)
  yield takeEvery(GET_CATEGORY_FOR_FORM, getCategoryForForm)
  yield takeEvery(GET_TEASER_PRODUCTS, getTeaserProducts)
  yield takeEvery(SUBMIT_ORDER_FORM, submitOrderForm)
  yield takeEvery(GET_ORDER, getOrder)
  yield takeEvery(GET_PRODUCT_USER_TAGS, getProductUserTags)
  yield takeEvery(CLONE_PRODUCT, cloneProduct)
}

export default productsAndSubscriptionsSaga
